import React, { useState, useEffect } from 'react';
import { Layout, FileText, Phone, BookOpen, Package, Briefcase, Grid, Search, Plus, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api from '../utils/api';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  landing: Layout,
  about: FileText,
  contact: Phone,
  blog: BookOpen,
  product: Package,
  portfolio: Briefcase,
  other: Grid,
};

const CATEGORY_LABELS = {
  landing: 'Landing Page',
  about: 'About Us',
  contact: 'Contact',
  blog: 'Blog Post',
  product: 'Product/Service',
  portfolio: 'Portfolio',
  other: 'Other',
};

const TemplateSelectionModal = ({ open, onClose, onSelectTemplate, onStartBlank }) => {
  const [templates, setTemplates] = useState({ system: [], custom: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async (forceReseed = false) => {
    try {
      setLoading(true);
      const res = await api.get('/templates');
      let data = res.data;

      // Auto-seed if no system templates exist OR user explicitly asked.
      // With the improved backend seed endpoint, this is now idempotent —
      // it inserts only missing templates by name, so we can safely retry.
      if (forceReseed || (Array.isArray(data.system) && data.system.length === 0)) {
        try {
          const seedRes = await api.post('/templates/seed-defaults');
          // eslint-disable-next-line no-console
          console.log('[Templates] seed-defaults response:', seedRes.data);
          // Always refetch after a seed attempt so any newly inserted
          // templates land in the UI (even if only 0 were inserted this
          // time, `total_system` will tell us the real count).
          const newRes = await api.get('/templates');
          data = newRes.data;
        } catch (seedErr) {
          // eslint-disable-next-line no-console
          console.error('[Templates] seed-defaults failed:', seedErr?.response?.status, seedErr?.response?.data);
        }
      }

      setTemplates(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Templates] Failed to fetch templates:', error?.response?.status, error?.response?.data);
      toast.error('Could not load templates. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = (templateList) => {
    if (!searchQuery) return templateList;
    return templateList.filter(template =>
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const handleUseTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      const res = await api.get(`/templates/${selectedTemplate.template_id}`);
      onSelectTemplate(res.data);
      onClose();
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template');
    }
  };

  const TemplateCard = ({ template, isSystem }) => {
    const CategoryIcon = CATEGORY_ICONS[template.category] || Grid;
    const isSelected = selectedTemplate?.template_id === template.template_id;

    return (
      <div
        onClick={() => setSelectedTemplate(template)}
        className={`cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
          isSelected
            ? 'border-indigo-500 ring-2 ring-indigo-200'
            : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <div className="relative aspect-video bg-slate-100 overflow-hidden">
          {template.thumbnail_url ? (
            <img
              src={template.thumbnail_url}
              alt={template.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <CategoryIcon size={32} className="text-slate-300" />
            </div>
          )}
          {isSystem && (
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
              System
            </span>
          )}
          {isSelected && (
            <div className="absolute inset-0 bg-indigo-500/10 flex items-center justify-center">
              <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          )}
        </div>
        <div className="p-3">
          <h4 className="font-medium text-sm text-slate-900 truncate">{template.name}</h4>
          <p className="text-xs text-slate-500 truncate mt-0.5">
            {CATEGORY_LABELS[template.category] || template.category}
          </p>
        </div>
      </div>
    );
  };

  const allTemplates = [...filterTemplates(templates.system), ...filterTemplates(templates.custom)];
  const systemTemplates = filterTemplates(templates.system);
  const customTemplates = filterTemplates(templates.custom);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">Create New Page</DialogTitle>
          <p className="text-sm text-slate-500">Start from scratch or choose a template</p>
        </DialogHeader>

        {/* Start Blank Option */}
        <div className="flex gap-4 py-4 border-b">
          <Button
            variant="outline"
            data-testid="create-page-blank"
            className="flex-1 h-20 flex flex-col items-center justify-center gap-2 border-2 border-dashed hover:border-indigo-300 hover:bg-indigo-50"
            onClick={() => {
              onStartBlank();
              onClose();
            }}
          >
            <Plus size={24} className="text-slate-400" />
            <span className="font-medium">Start from Scratch</span>
          </Button>
        </div>

        {/* Search */}
        <div className="relative py-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Templates */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <Tabs defaultValue="all">
              <TabsList className="mb-4">
                <TabsTrigger value="all">All ({allTemplates.length})</TabsTrigger>
                <TabsTrigger value="system">System ({systemTemplates.length})</TabsTrigger>
                <TabsTrigger value="custom">My Templates ({customTemplates.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                {allTemplates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 space-y-3">
                    <p>No templates found</p>
                    <Button variant="outline" size="sm" onClick={() => fetchTemplates(true)} data-testid="templates-reseed-btn">
                      Reseed default templates
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {templates.system.map(t => (
                      <TemplateCard key={t.template_id} template={t} isSystem={true} />
                    ))}
                    {templates.custom.map(t => (
                      <TemplateCard key={t.template_id} template={t} isSystem={false} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="system">
                {systemTemplates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No system templates found
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {systemTemplates.map(t => (
                      <TemplateCard key={t.template_id} template={t} isSystem={true} />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="custom">
                {customTemplates.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <p>No custom templates yet</p>
                    <p className="text-sm mt-1">Save your pages as templates to see them here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {customTemplates.map(t => (
                      <TemplateCard key={t.template_id} template={t} isSystem={false} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t mt-4">
          <div className="text-sm text-slate-500">
            {selectedTemplate && (
              <span>Selected: <strong>{selectedTemplate.name}</strong></span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleUseTemplate}
              disabled={!selectedTemplate}
            >
              Use Selected Template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectionModal;
